<template>
  <div class="recruiting-detail">
    <div v-if="loading" class="text-center py-5">
      <md-empty-state md-label="Loading opportunity…"></md-empty-state>
    </div>

    <div v-else-if="notFound" class="text-center py-5">
      <md-empty-state
        md-label="Opportunity not found"
        md-description="It may have been removed or you may be on a different community.">
        <md-button class="md-primary md-raised" @click="goBackToList">Back to Recruiting</md-button>
      </md-empty-state>
    </div>

    <template v-else-if="opportunity">
      <md-card class="mt-20">
        <md-card-content class="header-meta">
          <div class="header-meta__status">
            <span :class="['status-pill', `status-pill--${opportunity.status}`]">
              <span class="status-pill__dot"></span>
              {{ statusLabel(opportunity.status) }}
            </span>
            <span v-if="isFavorited">
              <button type="button" class="btn custom-card-header-filter btn-transparent btn:focus"
                @click="toggleFavorite">
                <md-tooltip md-direction="bottom">unfavorite</md-tooltip>
                <i class="fa fa-star fa-lg gold-color" aria-hidden="true"></i>
              </button>
            </span>
            <span v-else>
              <button type="button" class="btn custom-card-header-filter btn-transparent btn:focus"
                @click="toggleFavorite">
                <md-tooltip md-direction="bottom">favorite</md-tooltip>
                <i class="far fa-star fa-lg" aria-hidden="true"></i>
              </button>
            </span>
          </div>
          <div v-if="availableActions.length" class="header-actions">
            <md-button
              class="md-raised md-round md-accent capitalize md-white-color send-matches-btn"
              :disabled="approvedCount === 0 || sendingMatches"
              @click="sendMatches"
            >
              {{ sendingMatches
                ? 'Sending…'
                : (approvedCount > 0 ? `Send Matches (${approvedCount})` : 'Send Matches') }}
            </md-button>
            <template v-if="opportunity.status === 'in_review'">
              <md-button
                class="md-raised md-round md-accent capitalize md-white-color status-btn"
                :style="{ backgroundColor: STATUS_COLORS.active }"
                :disabled="statusSaving"
                @click="applyStatusChange('active')"
              >
                Approve
              </md-button>
              <md-button
                class="md-raised md-round md-accent capitalize md-white-color status-btn"
                :style="{ backgroundColor: STATUS_COLORS.rejected }"
                :disabled="statusSaving"
                @click="applyStatusChange('rejected')"
              >
                Reject
              </md-button>
            </template>
            <span class="header-actions__label m-left-10">Change status to:</span>
            <md-button
              v-for="a in availableActions"
              :key="a.value"
              class="md-raised md-round md-accent capitalize md-white-color status-btn"
              :style="{ backgroundColor: STATUS_COLORS[a.value] }"
              :disabled="statusSaving"
              @click="applyStatusChange(a.value)"
            >
              {{ a.label }}
            </md-button>
          </div>
        </md-card-content>
      </md-card>

      <!-- Banner card (community-colored): title + company. Moved BELOW the
           actions bar per layout request. -->
      <md-card class="mt-20 header-banner" :style="communityStyle">
        <div class="header-banner__row">
          <div class="header-banner__main">
            <h2 class="header-banner__title">{{ opportunity.title }}</h2>
            <div class="header-banner__company">{{ opportunity.company }}</div>
          </div>
        </div>
      </md-card>

      <!-- (Matching engine UI moved to /recruiting-match/:opportunity_id.
           The accordion below shows approved matches for this opp; the
           "Recruiting" hyperlink at the top of that accordion is what
           takes admins to the matching engine.) -->
      <!-- Two-column body: job details (left) + submitter/posting (right) -->
      <div class="detail-body mt-30">
        <div class="detail-body__main">
          <md-card class="detail-card">
            <div class="detail-title">Description</div>
            <div v-if="opportunity.description" class="detail-scroll">
              {{ opportunity.description }}
            </div>
            <p v-else class="empty-state">No description provided.</p>
          </md-card>

          <md-card class="detail-card">
            <div class="detail-title">Required Skills</div>
            <div v-if="opportunity.skills && opportunity.skills.length" class="skills-chips">
              <span v-for="s in opportunity.skills" :key="s" class="skill-chip">{{ s }}</span>
            </div>
            <p v-else class="empty-state">No skills specified.</p>
          </md-card>

          <md-card class="detail-card">
            <div class="detail-title">Application</div>
            <div class="field-label">Application URL</div>
            <div class="field-value">
              <a
                v-if="opportunity.applicationUrl"
                :href="opportunity.applicationUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="field-value__link"
              >
                {{ opportunity.applicationUrl }}
              </a>
              <span v-else>{{ EMPTY }}</span>
            </div>
          </md-card>
        </div>

        <div class="detail-body__side">
          <!-- Key Details first, aligned under the header actions (SCRUM-679). -->
          <md-card class="detail-card">
            <div class="detail-title">Key Details</div>
            <div class="field-grid">
              <div class="field-cell">
                <div class="field-label">Location</div>
                <div class="field-value">{{ opportunity.location || EMPTY }}</div>
              </div>
              <div class="field-cell">
                <div class="field-label">Job Type</div>
                <div class="field-value">{{ opportunity.jobType || EMPTY }}</div>
              </div>
              <div class="field-cell">
                <div class="field-label">Industry</div>
                <div class="field-value">
                  {{ opportunity.industry && opportunity.industry.length
                    ? opportunity.industry.join(', ')
                    : EMPTY }}
                </div>
              </div>
              <div class="field-cell">
                <div class="field-label">Salary</div>
                <div class="field-value">
                  {{ opportunity.salary && opportunity.salary.display
                    ? opportunity.salary.display
                    : EMPTY }}
                </div>
              </div>
              <div class="field-cell">
                <div class="field-label">Required Education</div>
                <div class="field-value">{{ displayEducation(opportunity.requiredEducation) }}</div>
              </div>
              <div class="field-cell">
                <div class="field-label">Required Experience</div>
                <div class="field-value">{{ displayExperience(opportunity.requiredExperienceYears) }}</div>
              </div>
            </div>
          </md-card>
          <md-card class="detail-card">
            <div class="detail-title">Posting Info</div>
            <div class="field-cell">
              <div class="field-label">Submitted by</div>
              <div class="field-value">{{ submitterDisplay }}</div>
            </div>
            <div class="field-cell">
              <div class="field-label">Date Submitted</div>
              <div class="field-value">{{ displayDate(opportunity.submittedDate) }}</div>
            </div>
            <div class="field-cell">
              <div class="field-label">Expiration Date</div>
              <div
                class="field-value"
                :class="{ 'field-value--expired': isExpired }"
              >{{ displayDate(opportunity.deadline || opportunity.expirationDate) }}</div>
            </div>
            <div class="field-cell">
              <div class="field-label">Submitted Via</div>
              <div class="field-value">{{ displaySourceChannel(opportunity.source) }}</div>
            </div>
          </md-card>
        </div>
      </div>

      <!-- Matches accordion -->
      <md-card class="mt-30">
        <md-card-expand>
          <md-card-actions md-alignment="left">
            <md-card-expand-trigger>
              <md-button class="expand-button-size">
                <strong class="upper-word">Matches ({{ matchesInFlight.length }})</strong>
              </md-button>
            </md-card-expand-trigger>
            <router-link
              :to="`/recruiting-match/${$route.params.opportunity_id}`"
              class="matches-recruiting-link upper-word"
            >
              Recruiting
            </router-link>
          </md-card-actions>
          <md-card-expand-content>
            <md-card-content>
              <p v-if="matchesInFlight.length === 0" class="empty-state">
                No matches approved yet. Click "Recruiting" above to run the
                matching engine.
              </p>
              <template v-else>
                <!-- Bulk bar for the accordion -->
                <div v-if="accordionSelectedIds.length > 0" class="matching-bulk-bar">
                  <span class="matching-bulk-bar__count">
                    <strong>{{ accordionSelectedIds.length }}</strong> selected
                  </span>
                  <md-button
                    class="md-raised md-accent md-round capitalize md-white-color md-orange"
                    @click="removeSelectedApprovedMatches"
                  >
                    Remove selected ({{ accordionSelectedIds.length }})
                  </md-button>
                  <md-button
                    class="md-raised md-round capitalize m-left-10"
                    @click="clearAccordionSelection"
                  >
                    Clear
                  </md-button>
                </div>

                <md-table class="matching-table">
                  <md-table-row>
                    <md-table-head class="card-header matching-select-col">
                      <md-checkbox v-model="allAccordionSelectedProxy" />
                    </md-table-head>
                    <md-table-head class="card-header">Candidate</md-table-head>
                    <md-table-head class="card-header">Score</md-table-head>
                    <md-table-head class="card-header">Match reasons</md-table-head>
                    <md-table-head class="card-header">Email</md-table-head>
                    <md-table-head class="card-header">Status</md-table-head>
                    <md-table-head class="card-header matching-actions-col">Actions</md-table-head>
                  </md-table-row>
                  <md-table-row v-for="m in matchesInFlight" :key="m.advisor_id">
                    <md-table-cell class="matching-select-col">
                      <md-checkbox
                        v-if="m.status === 'approved'"
                        v-model="accordionSelectedIds"
                        :value="m.advisor_id"
                      />
                    </md-table-cell>
                    <md-table-cell>
                      <router-link
                        :to="`/members/${m.advisor_id}`"
                        class="match-name-link"
                      >{{ m.name }}</router-link>
                      <div class="match-sub">
                        {{ ((m.dimensions && m.dimensions.location) || []).slice(0, 1).join('') }}
                        <span v-if="m.dimensions && m.dimensions.education && m.dimensions.education.length">
                          · {{ m.dimensions.education[0] }}
                        </span>
                      </div>
                    </md-table-cell>
                    <md-table-cell>{{ m.score + '%' }}</md-table-cell>
                    <md-table-cell>
                      <span
                        v-for="tag in matchTags(m)"
                        :key="tag"
                        class="skill-chip match-chip"
                      >{{ tag }}</span>
                    </md-table-cell>
                    <md-table-cell class="match-email">
                      {{ m.email || '—' }}
                    </md-table-cell>
                    <md-table-cell>
                      <span :class="statusColorClass(m.status)">{{ statusMatchLabel(m.status) }}</span>
                    </md-table-cell>
                    <md-table-cell class="matching-actions-col">
                      <md-button
                        v-if="m.status === 'approved'"
                        class="md-raised md-accent md-round capitalize md-white-color m-left-10 md-orange"
                        @click="removeApprovedMatch(m.advisor_id)"
                      >
                        Remove
                      </md-button>
                    </md-table-cell>
                  </md-table-row>
                </md-table>
              </template>
            </md-card-content>
          </md-card-expand-content>
        </md-card-expand>
      </md-card>

      <!-- Feedback collapse — driven by match.engagement.feedback from the alumni form. -->
      <md-card class="mt-30">
        <md-card-expand>
          <md-card-actions md-alignment="space-between">
            <md-card-expand-trigger>
              <md-button class="expand-button-size">
                <strong class="upper-word">Feedback ({{ feedbackEntries.length }})</strong>
              </md-button>
            </md-card-expand-trigger>
          </md-card-actions>
          <md-card-expand-content>
            <md-card-content>
              <div v-if="feedbackEntries.length" class="feedback-list">
                <div v-for="entry in feedbackEntries" :key="entry.id" class="feedback-row">
                  <div class="feedback-row__header">
                    <router-link
                      v-if="entry.memberId"
                      :to="`/members/${entry.memberId}`"
                      class="feedback-name"
                    >
                      {{ entry.memberName }}
                    </router-link>
                    <span v-else class="feedback-name feedback-name--muted">
                      {{ entry.memberName }}
                    </span>
                    <span class="feedback-stars" :aria-label="`${entry.rating} of 5 stars`">
                      <span
                        v-for="n in 5"
                        :key="n"
                        :class="['feedback-star', n <= entry.rating ? 'feedback-star--filled' : '']"
                      >★</span>
                    </span>
                    <span class="feedback-when">{{ displayDateTime(entry.submittedAt) }}</span>
                  </div>
                  <p v-if="entry.note" class="feedback-note">{{ entry.note }}</p>
                  <p v-else class="feedback-note feedback-note--muted">No comment.</p>
                </div>
              </div>
              <p v-else class="empty-state">No feedback submitted yet.</p>
            </md-card-content>
          </md-card-expand-content>
        </md-card-expand>
      </md-card>

      <!-- Activity collapse (statusHistory) -->
      <md-card class="mt-30">
        <md-card-expand>
          <md-card-actions md-alignment="space-between">
            <md-card-expand-trigger>
              <md-button class="expand-button-size">
                <strong class="upper-word">Activity ({{ activityEntries.length }})</strong>
              </md-button>
            </md-card-expand-trigger>
          </md-card-actions>
          <md-card-expand-content>
            <md-card-content>
              <div v-if="activityEntries.length" class="activity-log">
                <div v-for="(entry, i) in activityEntries" :key="entry._id || i" class="activity">
                  <b>
                    {{ entry.when | momentDateUTC }}
                    <span v-if="entry.memberName">- {{ entry.memberName }}</span>
                  </b>
                  <p class="ask-description" v-if="entry.description">{{ entry.description }}</p>
                  <p class="ask-description" v-if="entry.details">{{ entry.details }}</p>
                </div>
              </div>
              <p v-else class="empty-state">No activity yet.</p>
            </md-card-content>
          </md-card-expand-content>
        </md-card-expand>
      </md-card>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import { isFavorite, setFavorite } from '../services/recruitingFavoritesStore'
var moment = require('moment-timezone')
import API from '../services/API'
import {
  CORE_API_URL,
  coreApiOpportunityToHermes,
  statusLabel
} from '../services/opportunityAdapter'
import {
  getApprovedMatches,
  getApprovedCount,
  getRunState,
  setMatchStatus,
} from '../services/recruitingMatchesStore'

const EMPTY = '—'

const EDUCATION_LABELS = {
  masters: "Master's degree",
  bachelors: "Bachelor's degree",
  'high-school': 'High School diploma'
}

const SOURCE_LABELS = {
  'program-page': 'Program Page',
  dashboard: 'Dashboard',
  bulk: 'Bulk (job-board ingestion)'
}

// Full set of statuses, labels sourced from STATUS_LABELS so the wording
// stays in one place. The detail view exposes them as change-status buttons
// (minus the current status, and minus the ones Approve/Reject already
// cover while in_review — see availableActions).
const STATUS_ORDER = ['in_review', 'active', 'rejected', 'incomplete', 'quarantined', 'closed']
const ALL_STATUSES = STATUS_ORDER.map((value) => ({ value, label: statusLabel(value) }))

// Solid fill per status — same intent as hermes RequestDetail action buttons
// (md-accent green + inline overrides like #F2C057 for Request Support).
const STATUS_COLORS = {
  in_review: '#f59e0b',
  active: '#28a745',
  rejected: '#dc3545',
  incomplete: '#3b82f6',
  quarantined: '#475569',
  closed: '#6c757d'
}

export default {
  name: 'RecruitingDetail',
  data() {
    return {
      api: new API(),
      opportunity: null,
      loading: true,
      notFound: false,
      statusSaving: false,
      // Favorite flag (hermes-only, persisted client-side).
      isFavorited: false,
      communityStyle: { backgroundColor: 'white', color: '#101828' },
      // Approved-only slice of matches for this opp (still pending Send).
      approvedMatches: [],
      // Matches already sent — kept alongside approved so the accordion
      // preserves the full history instead of dropping rows post-send.
      sentMatches: [],
      // Matches from core-api. Rows with engagement.feedback drive the Feedback tab.
      persistedMatches: [],
      sendingMatches: false,
      // Multiselect in the accordion. Cleared on remove / undo / reload.
      accordionSelectedIds: [],
      EMPTY,
      STATUS_COLORS
    }
  },
  computed: {
    // Submitter for the "Submitted by" row.
    submitterDisplay() {
      const s = this.opportunity && this.opportunity.source
      if (s) {
        if (s.email) return s.email
        const name = [s.firstName, s.lastName].filter(Boolean).join(' ').trim()
        if (name) return name
      }
      return 'Bulk-ingested'
    },
  
    isExpired() {
      const iso = this.opportunity &&
        (this.opportunity.deadline || this.opportunity.expirationDate)
      if (!iso) return false
      return moment(iso).isBefore(moment(), 'day')
    },
    availableActions() {
      if (!this.opportunity) return []
      const isReview = this.opportunity.status === 'in_review'
      return ALL_STATUSES.filter((s) => {
        if (s.value === this.opportunity.status) return false
        // While in_review, the Approve/Reject buttons own these two.
        if (isReview && (s.value === 'active' || s.value === 'rejected')) return false
        return true
      })
    },
    approvedCount() {
      return this.approvedMatches.length
    },
    // DB is the single source of truth so any user/browser sees the same matches.
    matchesInFlight() {
      const POST_INTERACTION_STATUSES = new Set([
        'approved', 'sent', 'reminder_sent',
        'clicked', 'not_interested', 'referred', 'applied', 'secured',
        'bounced', 'unsubscribed', 'suppressed', 'expired',
      ])
      return (this.persistedMatches || [])
        .filter((m) => {
          if (!m) return false
          if (POST_INTERACTION_STATUSES.has(m.status)) return true
          if (m.status === 'pending_send' && m.email && m.email.mercuryId) return true
          return false
        })
        .map((m) => this.dbMatchToLocal(m))
    },
    allAccordionSelected() {
      if (!this.approvedMatches.length) return false
      return this.approvedMatches.every(
        (m) => this.accordionSelectedIds.indexOf(m.advisor_id) !== -1,
      )
    },
    allAccordionSelectedProxy: {
      get() {
        return this.allAccordionSelected
      },
      set(newVal) {
        if (newVal) {
          this.accordionSelectedIds = this.approvedMatches.map(
            (m) => m.advisor_id,
          )
        } else {
          this.accordionSelectedIds = []
        }
      },
    },
    // One row per persisted match with engagement.feedback; newest first.
    // `m.member` is populated by core-api with a name sliver; falls back to raw ObjectId.
    feedbackEntries() {
      return (this.persistedMatches || [])
        .filter((m) => m && m.engagement && m.engagement.feedback)
        .map((m) => {
          const populated = m.member && typeof m.member === 'object'
          const memberId = populated ? m.member._id : m.member
          const first = populated ? (m.member.first || '') : ''
          const last = populated ? (m.member.last || '') : ''
          const displayName = populated ? (m.member.displayName || '') : ''
          const name = [first, last].filter(Boolean).join(' ').trim() || displayName || 'Alumnus'
          return {
            id: m._id,
            memberId,
            memberName: name,
            rating: (m.engagement.feedback && m.engagement.feedback.rating) || 0,
            note: (m.engagement.feedback && m.engagement.feedback.note) || '',
            submittedAt:
              (m.engagement.feedback && m.engagement.feedback.submittedAt) ||
              m.updatedAt ||
              m.createdAt,
          }
        })
        .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt))
    },
    // Unified activity feed for the opportunity page — merges opp-level
    // activityLog with per-match activityLog
    activityEntries() {
      // Bulk-source opps → override the description for opportunity_created so
      // the audit trail reads "Created via CSV import" instead of the generic label.
      const isBulk = this.opportunity && this.opportunity.source && this.opportunity.source.channel === 'bulk'
      const oppEntries = ((this.opportunity && this.opportunity.activityLog) || []).map((e) => ({
        ...e,
        memberName: this.normalizeMemberName(e.memberName),
        description:
          e.action === 'opportunity_created' && isBulk
            ? 'Created via CSV import'
            : e.description,
      }))
      const matchEntries = (this.persistedMatches || []).flatMap((m) => {
        const populated = m && m.member && typeof m.member === 'object'
        const first = populated ? (m.member.first || '') : ''
        const last = populated ? (m.member.last || '') : ''
        const displayName = populated ? (m.member.displayName || '') : ''
        const fallbackName =
          [first, last].filter(Boolean).join(' ').trim() || displayName || ''
        // "match created" is an algorithm event (a candidate was generated), not an
        // admin action — hide it so Activity only shows real events (SCRUM-678).
        return (m.activityLog || [])
          .filter((e) => e && e.action !== 'match_created')
          .map((e) => ({
            ...e,
            memberName: fallbackName || this.normalizeMemberName(e.memberName),
          }))
      })
      return [...oppEntries, ...matchEntries].sort(
        (a, b) => new Date(b.when || 0) - new Date(a.when || 0),
      )
    },
  },
  async created() {
    await this.loadOpportunity()
    this.reloadApprovedMatches()
    // Awaited so Feedback (N) is accurate on first paint (not after user clicks).
    await this.loadPersistedMatches()
  },
  methods: {
    statusLabel,
    statusMatchLabel(status) {
      const labels = {
        pending_send: 'Pending Send',
        approved: 'Approved',
        rejected: 'Rejected',
        sent: 'Sent',
        reminder_sent: 'Reminder Sent',
        clicked: 'Clicked',
        not_interested: 'Not Interested',
        referred: 'Referred',
        applied: 'Applied',
        secured: 'Secured',
        bounced: 'Bounced',
        unsubscribed: 'Unsubscribed',
        suppressed: 'Suppressed',
        expired: 'Expired',
      }
      return labels[status] || status || 'N/A'
    },
    statusColorClass(status) {
      if (status === 'clicked') return 'md-black-color'
      if (['sent', 'reminder_sent'].includes(status)) return 'md-blue-color'
      if (['approved', 'applied', 'secured', 'referred'].includes(status)) return 'md-green-color'
      if (['rejected', 'bounced', 'unsubscribed'].includes(status)) return 'md-red-color'
      return 'md-orange-color'
    },
    normalizeMemberName(name) {
      if (!name) return 'System'
      return name.toLowerCase() === 'system' ? 'System' : name
    },
    goBackToList() {
      this.$router.push('/recruiting')
    },
    // Toggle the favorite flag (hermes-only, client-side).
    toggleFavorite() {
      if (!this.opportunity) return
      this.isFavorited = setFavorite(this.opportunity._id, !this.isFavorited)
    },
    displayDate(iso) {
      if (!iso) return EMPTY
      return moment(iso).format('MM/DD/YYYY')
    },
    displayDateTime(iso) {
      if (!iso) return EMPTY
      return moment(iso).format('MM/DD/YYYY h:mm A')
    },
    // Shape a persisted match doc into the row format the Matches accordion renders.
    // Kept in sync with dbMatchToLocal in RecruitingMatch.vue.
    dbMatchToLocal(m) {
      const populated = m.member && typeof m.member === 'object'
      const memberId = populated ? m.member._id : m.member
      const first = populated ? (m.member.first || '') : ''
      const last = populated ? (m.member.last || '') : ''
      const displayName = populated ? (m.member.displayName || '') : ''
      const name =
        [first, last].filter(Boolean).join(' ').trim() ||
        displayName ||
        `Member ${String(memberId || '').slice(-6)}`
      const rawStatus = m.status || 'pending'
      const dispatched = m.email && m.email.mercuryId
      const status = (rawStatus === 'pending_send' && dispatched) ? 'sent' : rawStatus
      return {
        _id: m._id,
        match_id: m._id,
        advisor_id: memberId,
        name,
        email: (populated && Array.isArray(m.member.emails) && m.member.emails[0]) || '',
        score: Math.round((m.similarity || 0) * 100),
        match_facets: m.matchFacets || [],
        dimensions: {
          location: [],
          education: [],
          skills:
            populated && m.member.dimensions && Array.isArray(m.member.dimensions.skills)
              ? m.member.dimensions.skills
              : [],
        },
        industry: populated && Array.isArray(m.member.industry) ? m.member.industry : [],
        status,
      }
    },
    async loadPersistedMatches() {
      const oppId = this.$route.params.opportunity_id
      try {
        const token = localStorage.getItem('auth0_token')
        const { data } = await axios.get(
          `${CORE_API_URL}/api/opportunity-matches/by-opportunity/${oppId}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        )
        this.persistedMatches = Array.isArray(data) ? data : []
      } catch (e) {
        // Non-fatal — Feedback tab just stays empty.
        this.persistedMatches = []
      }
    },
    // Flip any locally-"approved" match to "sent" once core-api reports it as
    // actually dispatched. Keeps the Send counter honest when the /review
    // response was lost (gateway timeout) but the backend already sent.
    syncLocalStatusesFromPersisted(oppId) {
      const DISPATCHED = new Set([
        'sent', 'reminder_sent', 'clicked', 'not_interested',
        'referred', 'applied', 'secured', 'bounced', 'unsubscribed',
      ])
      ;(this.persistedMatches || []).forEach((m) => {
        if (!m) return
        const dispatched =
          DISPATCHED.has(m.status) ||
          (m.status === 'pending_send' && m.email && m.email.mercuryId)
        if (dispatched) setMatchStatus(oppId, this.dbMatchToLocal(m).advisor_id, 'sent')
      })
    },
    displayEducation(v) {
      if (!v) return EMPTY
      return EDUCATION_LABELS[v] || v
    },
    displayExperience(y) {
      if (y == null) return EMPTY
      return y + (y === 1 ? ' year' : ' years')
    },
    displaySourceChannel(src) {
      if (!src || !src.channel) return EMPTY
      return SOURCE_LABELS[src.channel] || src.channel
    },
    async loadOpportunity() {
      this.loading = true
      this.notFound = false
      const id = this.$route.params.opportunity_id
      try {
        const token = localStorage.getItem('auth0_token')
        const { data } = await axios.get(
          `${CORE_API_URL}/api/opportunities/${id}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        )
        const adapted = coreApiOpportunityToHermes(data)
        if (!adapted) { this.notFound = true; return }
        this.opportunity = adapted
        this.isFavorited = isFavorite(adapted._id)
        // Fire-and-forget community styling — banner background matches
        // the community's brand color (same pattern as the list rows).
        // `communityRel` (ObjectId) isn't always populated on legacy opps, so
        // we fall back to the currently-selected community_id (the source of
        // truth used by the list too).
        const commId = adapted.communityId || localStorage.getItem('community_id')
        if (commId) this.fetchCommunityStyling(commId)
      } catch (e) {
        this.notFound = true
      } finally {
        this.loading = false
      }
    },
    // ─── Matches accordion (read-only view of the /recruiting-match store) ───
    matchTags(m) {
      const skills = ((m && m.dimensions && m.dimensions.skills) || []).slice(0, 3)
      const industries = ((m && m.industry) || []).slice(0, 1)
      return skills.concat(industries)
    },
    reloadApprovedMatches() {
      const oppId = this.$route.params.opportunity_id
      this.approvedMatches = getApprovedMatches(oppId)
      // Also pull the 'sent' rows so the accordion keeps the send history.
      const state = getRunState(oppId)
      const allMatches = (state && state.matches) || []
      this.sentMatches = allMatches.filter((m) => m.status === 'sent')
    },
    removeApprovedMatch(advisorId) {
      const oppId = this.$route.params.opportunity_id
      // "Remove" from the accordion = flip status back to pending in the
      // shared store. If the user goes back to /recruiting-match/:id the
      // row shows Pending again with Approve/Reject buttons.
      setMatchStatus(oppId, advisorId, 'pending')
      // Drop it from the current selection so the counter matches reality.
      const i = this.accordionSelectedIds.indexOf(advisorId)
      if (i !== -1) this.accordionSelectedIds.splice(i, 1)
      this.reloadApprovedMatches()
    },
    clearAccordionSelection() {
      this.accordionSelectedIds = []
    },
    removeSelectedApprovedMatches() {
      const oppId = this.$route.params.opportunity_id
      this.accordionSelectedIds.forEach((id) => {
        setMatchStatus(oppId, id, 'pending')
      })
      this.accordionSelectedIds = []
      this.reloadApprovedMatches()
    },
    async sendMatches() {
      if (this.approvedCount === 0) return
      // Re-entry guard: stacked /review calls that hang would exhaust the
      // browser's per-host socket limit and freeze every other API call.
      if (this.sendingMatches) return
      const oppId = this.$route.params.opportunity_id
      this.sendingMatches = true
      try {
        // Pull the full run state so we can extract both approved and
        // rejected match _ids (not advisor_ids — core-api expects the
        // OpportunityMatch document ids for /review).
        const state = getRunState(oppId)
        const allMatches = (state && state.matches) || []
        const confirmedList = allMatches.filter((m) => m.status === 'approved')
        const confirmedMatchIds = confirmedList
          .map((m) => m._id || m.match_id)
          .filter(Boolean)
        const excludedMatchIds = allMatches
          .filter((m) => m.status === 'rejected')
          .map((m) => m._id || m.match_id)
          .filter(Boolean)

        const token = localStorage.getItem('auth0_token')
        const authHeader = token ? { Authorization: `Bearer ${token}` } : {}

        // POST /api/matching/:opportunityId/review — confirmed matches
        // trigger a templated email send, excluded matches are suppressed
        // and reported to the vector DB.
        // If /review hangs (server or gateway), abort after 30s and release
        // the socket instead of leaving it open forever. Never fires on the happy path.
        const resp = await axios.post(
          `${CORE_API_URL}/api/matching/${oppId}/review`,
          {
            confirmedMatchIds,
            excludedMatchIds,
            excludeReason: 'excluded_by_reviewer',
          },
          { headers: authHeader, timeout: 30000 },
        )

        // Surface what core-api actually did: a partial failure (e.g. SendGrid
        // down) or skipped matches must not read as a clean success. Falls back
        // to "all queued" if the backend doesn't yet return the breakdown.
        const c = (resp && resp.data && resp.data.confirmed) || null
        const sent = c && typeof c.sent === 'number' ? c.sent : confirmedMatchIds.length
        const failed = c && typeof c.failed === 'number' ? c.failed : 0
        const skipped = c && Array.isArray(c.skipped) ? c.skipped.length : 0
        if (this.$toast) {
          if (failed || skipped) {
            this.$toast.error(
              `Sent ${sent}/${confirmedMatchIds.length}. ${failed} failed, ${skipped} skipped — the rest stay approved so you can retry.`,
            )
          } else {
            this.$toast.success(`${sent} matches queued to receive the opportunity.`)
          }
        }
      } catch (err) {
        console.error('[recruiting/matching] sendMatches failed:', err)
        // axios flags a timed-out request with code ECONNABORTED.
        const timedOut = err && err.code === 'ECONNABORTED'
        if (this.$toast) {
          this.$toast.error(
            timedOut
              ? 'Sending matches timed out. The counter now reflects what actually went out — refresh if in doubt.'
              : 'Failed to send matches. Please try again.',
          )
        }
      } finally {
        // Reconcile from core-api (source of truth) whatever the HTTP outcome:
        // /review can time out at the gateway after the backend already sent
        // some emails, so the Send counter must follow the DB, not the response.
        await this.loadPersistedMatches()
        this.syncLocalStatusesFromPersisted(oppId)
        this.reloadApprovedMatches()
        this.sendingMatches = false
      }
    },
    async fetchCommunityStyling(communityId) {
      try {
        const res = await this.api.get(`communities/${communityId}/community-request`)
        const cs = res && res.data && res.data.customSettings
        if (cs && cs.primaryColor) {
          this.communityStyle = {
            backgroundColor: cs.primaryColor,
            color: cs.secondaryColor || '#fff'
          }
        }
      } catch (e) {
        // fall back to defaults; not fatal
      }
    },
    async applyStatusChange(newStatus) {
      if (!this.opportunity || newStatus === this.opportunity.status) return
      this.statusSaving = true
      try {
        const token = localStorage.getItem('auth0_token')
        const { data } = await axios.patch(
          `${CORE_API_URL}/api/opportunities/${this.opportunity._id}`,
          { status: newStatus },
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        )
        const adapted = coreApiOpportunityToHermes(data) || this.opportunity
        adapted.status = adapted.status || newStatus
        this.opportunity = adapted
        if (this.$toast) this.$toast.success(`Status updated to ${statusLabel(newStatus)}`)
      } catch (e) {
        if (this.$toast) this.$toast.error('Could not update status. Please try again.')
      } finally {
        this.statusSaving = false
      }
    }
  }
}
</script>

<style scoped>
.recruiting-detail { padding: 20px; }

/* Community-colored banner card: title + company + status pill. */
.header-banner { padding: 20px 24px; }
.header-banner__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}
.header-banner__main { min-width: 0; }
.header-banner__title { margin: 0; font-weight: 700; color: inherit; }
.header-banner__company { margin-top: 4px; font-weight: 500; font-size: 1.6rem; color: inherit; opacity: 0.85; }

/* Meta + actions card (below the banner). */
.header-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 12px 16px;
}
.header-meta__status { display: flex; align-items: center; gap: 8px; }
.header-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; justify-content: flex-end; }
.header-actions__label { font-size: 13px; color: #6c757d; margin-right: 4px; }

/* Status pills — solid-fill, same style as the list rows (RecruitingRow.vue).
   Sitting on the community-colored banner, so a subtle white halo keeps them
   legible on any brand color. */
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  border-radius: 999px;
  font-size: 1.75rem;
  font-weight: 700;
  color: #fff;
  border: 1.5px solid rgba(255, 255, 255, 0.75);
  white-space: nowrap;
}
.status-pill__dot { width: 9px; height: 9px; border-radius: 50%; background: currentColor; opacity: 0.85; }
.status-pill--active     { background: #28a745; }
.status-pill--in_review  { background: #f59e0b; }
.status-pill--closed     { background: #6c757d; }
.status-pill--rejected   { background: #dc3545; }
.status-pill--incomplete { background: #3b82f6; }
.status-pill--quarantined { background: #475569; }

/* Change-status buttons: hermes RequestDetail style
   (md-raised md-round md-accent capitalize md-white-color) with a
   per-status background override — same pattern as the "Request Support"
   inline #F2C057 in RequestDetail.vue. */
.status-btn {
  color: #fff !important;
}
.status-btn.md-button.md-theme-default.md-raised:not([disabled]) {
  color: #fff !important;
}

.detail-body { display: grid; grid-template-columns: 2fr 1fr; gap: 20px; }
@media (max-width: 900px) {
  .detail-body { grid-template-columns: 1fr; }
}
.detail-card { padding: 16px; margin-bottom: 16px; }
.detail-title { font-size: 13px; font-weight: 700; text-transform: uppercase; color: #667085; margin-bottom: 12px; letter-spacing: 0.4px; }
.detail-scroll { max-height: 320px; overflow-y: auto; }
.field-cell { margin-bottom: 12px; }
.field-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 24px; }
.field-label { font-size: 12px; color: #667085; margin-bottom: 4px; }
.field-value { font-size: 14px; color: #101828; word-break: break-word; }
.field-value--expired { color: #dc3545; font-weight: 700; }
.field-value__link { color: #2563eb; text-decoration: none; }
.field-value__link:hover { text-decoration: underline; }
.empty-state { color: #98a2b3; font-style: italic; margin: 0; }

.skills-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.skill-chip { background: #eef2ff; color: #3730a3; padding: 4px 10px; border-radius: 10px; font-size: 12px; }

/* Matches accordion */
.matches-recruiting-link {
  font-size: 18px;
  font-weight: 700;
  margin-top: 2px;
  color: #6b7280;
  align-self: center;
}
.matches-recruiting-link:hover { color: #111827; text-decoration: underline; }
.match-name-link { font-weight: 600; color: #111827; text-decoration: none; }
.match-name-link:hover { text-decoration: underline; }
.match-sub { font-size: 12px; color: #6c757d; margin-top: 2px; }
.match-chip { display: inline-block; margin: 2px 4px 2px 0; }
.send-matches-btn { margin-right: 4px; }
.matching-actions-col { text-align: left; }
.matching-select-col { width: 56px; padding-left: 16px !important; }
.matching-select-col >>> .md-checkbox { margin: 0; }
.match-email { font-size: 13px; color: #374151; }
.matching-bulk-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 10px 12px;
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  border-radius: 6px;
}
.matching-bulk-bar__count { font-size: 14px; color: #3730a3; margin-right: 8px; }

.activity-log { display: flex; flex-direction: column; gap: 8px; }
.activity-row { display: flex; gap: 12px; align-items: center; }
.activity-when { color: #667085; font-size: 13px; }
.activity-who { color: #98a2b3; font-size: 12px; }

.feedback-list { display: flex; flex-direction: column; gap: 16px; }
.feedback-row {
  padding: 12px 14px;
  background: #f9fafb;
  border: 1px solid #eef2f7;
}
.feedback-row__header { display: flex; gap: 12px; align-items: center; margin-bottom: 6px; }
.feedback-name { color: #3730a3; font-weight: 600; font-size: 14px; text-decoration: none; }
.feedback-name:hover { text-decoration: underline; }
.feedback-name--muted { color: #98a2b3; font-weight: 500; }
.feedback-stars { display: inline-flex; gap: 2px; color: #d1d5db; font-size: 18px; line-height: 1; }
.feedback-star--filled { color: #f59e0b; }
.feedback-when { color: #667085; font-size: 13px; margin-left: auto; }
.feedback-note { margin: 0; color: #101828; font-size: 14px; line-height: 1.4; }
.feedback-note--muted { color: #98a2b3; font-style: italic; }

/* Fallback in case the global md-*-color class from Vue Material isn't
   applied inside this scope — matches the green shade used for Approved. */
.md-blue-color { color: #2563eb; font-weight: 600; }
.md-green-color { color: #059669; font-weight: 600; }
.md-black-color { color: #101828; font-weight: 600; }
</style>



// WEBPACK FOOTER //
// src/components/RecruitingDetail.vue